import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQueries,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query"
import { AxiosError } from "axios"

import { ICalendarEventParams } from "../../hooks/WidgetHooks"
import { getQueryClient } from "../../libs/reactQuery"
import { getAllAffiliates } from "../../services/AffiliateServices"
import { getAvailabilityForMonth } from "../../services/CalendarServices"
import { getAllCustomerMemberships } from "../../services/CustomerService"
import { getIntegrations } from "../../services/IntegrationServices"
import { getProductPricingByProdId } from "../../services/ProductPricingServices"
import { getGiftCardValidity, getProductAddons } from "../../services/ProductServices"
import { getAllPromoCode } from "../../services/PromoCodeServices"
import { getDomainOrderFieldbyId } from "../../services/TenantServices"
import { addNote, getAllNotes, validateOffer } from "../../services/ViewOrderServices"
import { GetAffiliatesResponseType } from "../../types/AffiliateTypes"
import { GetAllEventsResponseType } from "../../types/CalendarTypes"
import { GetCustomerMembershipsResponseType } from "../../types/CustomerTypes"
import { GiftCardValidityResponseType } from "../../types/GiftCardType"
import { GetIntegrationResponseType } from "../../types/IntegrationTypes"
import {
  ApplyMembershipToCartRequestType,
  GetAvailabilityForDateResponseType,
  GetAvailabilityForMonthResponseType,
  GetCustomersResponseType,
  GetProductFieldsResponseType,
  ProductCategoryType,
} from "../../types/OrderTypes"
import { GetAllProductPricingTypes } from "../../types/ProductPricingTypes"
import { GetProductAddonsResponseType } from "../../types/ProductTypes"
import { GetAllOffersResponseType } from "../../types/PromoCodeServiceTypes"
import {
  AddNoteRequestType,
  AddNoteResponseType,
  CustomerRebookRequestType,
  GetAllNotesResponseType,
  NoteTypesEnum,
} from "../../types/ViewOrderTypes"
import { CacheKeys } from "../../utils/CacheKeys"

import {
  addEditAddonsOfCart,
  addParticipantsToCart,
  addProductToCart,
  applyMembershipToCart,
  applyPromocode,
  cloneOrderToCart,
  createEditExpenseDiscount,
  deleteCartItem,
  deleteCartItems,
  getActiveProducts,
  getAllCustomers,
  getCart,
  getParticipantsFromCart,
  getPerBookingFieldsOfCart,
  getProductAvailabilityForDate,
  getProductAvailabilityForDateWithCallToBook,
  getProductAvailabilityForMonth,
  getProductFields,
  initializeCart,
  removeAffiliateOfCart,
  removeMembershipFromCart,
  removePromocode,
  updateAffiliateOfCart,
  updateCartItem,
  updateCustomerOfCart,
  updatePerBookingFieldsOfCart,
} from "./services"
import {
  AddEditAddonsRequestType,
  AddEditCartItemRequestType,
  AddParticipantsToCartRequestType,
  AddParticipantsToCartResponseType,
  ApplyPromocodeRequestType,
  CreateCartResponseType,
  CreateEditExpenseDiscountRequestType,
  GenericSuccessfulResponseType,
  GetCartResponseType,
  GetOrderFieldsResponseType,
  GetParticipantsFromCart,
  GetPerBookingFieldsOfCartResponseType,
  UpdateAffiliateOfCartRequestType,
  UpdateCustomerOfCartRequestType,
  UpdatePerBookingFieldsOfCartRequestType,
  UpdatePerBookingFieldsOfCartResponseType,
  ValidateOfferResponseType,
} from "./types"

export interface GenericQueryOptionsType<
  responseType,
  errorResponseType = AxiosError<Record<string, string>>,
> {
  queryOptions?: Omit<UseQueryOptions<responseType, errorResponseType>, "queryFn" | "queryKey">
}
export interface GenericMutateOptionsType<
  variablesType = object,
  responseType = GenericSuccessfulResponseType,
  errorResponseType = AxiosError<Record<string, string>>,
> {
  mutateOptions?: Omit<
    UseMutationOptions<responseType, errorResponseType, variablesType>,
    "mutationFn"
  >
}

export interface UseGetCategoryListWithProductsProps
  extends GenericQueryOptionsType<ProductCategoryType[]> {
  customerId?: string
  isIntegratedPage?: boolean
}
export const useGetCategoryListWithProducts = ({
  queryOptions,
  customerId,
  isIntegratedPage = false,
}: UseGetCategoryListWithProductsProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.CATEGORY_LIST_WITH_PRODUCTS, isIntegratedPage, customerId],
    queryFn: async () => {
      const { data } = await getActiveProducts(isIntegratedPage, customerId)
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 30,
  })

export interface UseGetProductAvailabilityPerMonthDataProps
  extends GenericQueryOptionsType<GetAvailabilityForMonthResponseType> {
  productId: string
  date: string
}
export const useGetProductAvailabilityPerMonthData = ({
  queryOptions,
  date,
  productId,
}: UseGetProductAvailabilityPerMonthDataProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.PRODUCT_AVAILABILITY_PER_MONTH, productId, date],
    queryFn: async () => {
      const { data } = await getProductAvailabilityForMonth(productId, date)
      return data
    },
    placeholderData: keepPreviousData,
  })

export interface UseGetProductPriceDataProps
  extends GenericQueryOptionsType<GetAllProductPricingTypes> {
  productId: string
  slotId: string
}
export const useGetProductPriceData = ({
  queryOptions,
  slotId,
  productId,
}: UseGetProductPriceDataProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.PRODUCT_PRICE, productId, slotId],
    queryFn: async () => {
      const { data } = await getProductPricingByProdId(productId, {}, false, slotId || "")
      return data
    },
    placeholderData: keepPreviousData,
  })

export interface UseGetProductAvailabilityForDateProps
  extends GenericQueryOptionsType<GetAvailabilityForDateResponseType> {
  productId: string
  date: string
  isCallToBook?: boolean
}
export const useGetProductAvailabilityForDate = ({
  queryOptions,
  date,
  productId,
  isCallToBook = false,
}: UseGetProductAvailabilityForDateProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.PRODUCT_AVAILABILITY_PER_DATE, productId, date],
    queryFn: async () => {
      if (isCallToBook) {
        const { data } = await getProductAvailabilityForDateWithCallToBook({
          productId,
          date,
        })
        return data
      }
      const { data } = await getProductAvailabilityForDate({
        productId,
        date,
      })
      return data
    },
    placeholderData: keepPreviousData,
  })

export interface UseGetCartProps extends GenericQueryOptionsType<GetCartResponseType> {
  cartId: string
}
export const useGetCart = ({ queryOptions, cartId }: UseGetCartProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.CART, cartId],
    queryFn: async () => {
      const { data } = await getCart(cartId)
      return data
    },
    staleTime: 1000 * 60 * 30,
  })

export const useInitializeCartMutation = ({ mutateOptions }: GenericMutateOptionsType) => {
  return useMutation({
    mutationFn: async () => {
      const { data } = await initializeCart()
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, data.cart.id], {
        data: data?.cart,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })
}

export const useAddProductToCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  payload: AddEditCartItemRequestType
  cartId: string
}>) =>
  useMutation({
    mutationFn: async ({ cartId, payload }) => {
      const { data } = await addProductToCart(cartId, payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      const client = getQueryClient()
      await client.setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.data,
      })
      await client.refetchQueries({
        queryKey: [CacheKeys.CART_PARTICIPANTS, variables.cartId, false],
        exact: true,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useUpdateCartItemMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  payload: AddEditCartItemRequestType
  cartId: string
  cartItemId: string
}>) =>
  useMutation({
    mutationFn: async ({ cartId, cartItemId, payload }) => {
      const { data } = await updateCartItem(cartId, cartItemId, payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      const client = getQueryClient()
      await client.setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.data,
      })
      await client.invalidateQueries({
        queryKey: [CacheKeys.CART_PARTICIPANTS, variables.cartId, false],
        exact: true,
        refetchType: "all",
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useDeleteCartItemMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  cartId: string
  cartItemId: string
}>) =>
  useMutation({
    mutationFn: async ({ cartId, cartItemId }) => {
      const { data } = await deleteCartItem(cartId, cartItemId)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.cart,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useDeleteMultipleCartItemsMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  cartId: string
  cartItemIds: string[]
}>) =>
  useMutation({
    mutationFn: async ({ cartId, cartItemIds }) => {
      const { data } = await deleteCartItems(cartId, cartItemIds)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.cart,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useUpdateCustomerOfCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  payload: UpdateCustomerOfCartRequestType
  cartId: string
}>) =>
  useMutation({
    mutationFn: async ({ cartId, payload }) => {
      const { data } = await updateCustomerOfCart(cartId, payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.data,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useApplyPromocodeToCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  cartId: string
  payload: ApplyPromocodeRequestType
}>) =>
  useMutation({
    mutationFn: async ({ cartId, payload }) => {
      const { data } = await applyPromocode(cartId, payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      if (data?.code === 200) {
        await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
          data: data?.cart,
        })
      }
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useRemovePromocodeInCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  cartId: string
  isIntegratedPage?: boolean
}>) =>
  useMutation({
    mutationFn: async ({ cartId, isIntegratedPage }) => {
      const { data } = await removePromocode(cartId, isIntegratedPage)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.cart,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useApplyMembershipToCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  cartId: string
  payload: ApplyMembershipToCartRequestType
}>) =>
  useMutation({
    mutationFn: async ({ cartId, payload }) => {
      const { data } = await applyMembershipToCart(cartId, payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      if (data?.code === 200) {
        await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
          data: data?.cart,
        })
      }
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useRemoveMembershipInCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  cartId: string
  isIntegratedPage?: boolean
}>) =>
  useMutation({
    mutationFn: async ({ cartId, isIntegratedPage }) => {
      const { data } = await removeMembershipFromCart(cartId, isIntegratedPage)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.cart,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useUpdateAffiliateOfCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  payload: UpdateAffiliateOfCartRequestType
  cartId: string
}>) =>
  useMutation({
    mutationFn: async ({ cartId, payload }) => {
      const { data } = await updateAffiliateOfCart(cartId, payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.data,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useRemoveAffiliateOfCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  cartId: string
}>) =>
  useMutation({
    mutationFn: async ({ cartId }) => {
      const { data } = await removeAffiliateOfCart(cartId)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.data,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export interface UseGetAllCustomersProps extends GenericQueryOptionsType<GetCustomersResponseType> {
  filters: Record<string, string | number | boolean>
}
export const useGetAllCustomers = ({ queryOptions, filters }: UseGetAllCustomersProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.CUSTOMERS, filters],
    queryFn: async () => {
      const { data } = await getAllCustomers(filters)
      return data
    },
    placeholderData: keepPreviousData,
  })

export interface UseGetAllOffersProps extends GenericQueryOptionsType<GetAllOffersResponseType> {
  filters: Record<string, string | number | boolean>
}
export const useGetAllOffers = ({ queryOptions, filters }: UseGetAllOffersProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.OFFERS, filters],
    queryFn: async () => {
      const { data } = await getAllPromoCode(filters)
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  })

export interface UseGetAllAffiliatesProps
  extends GenericQueryOptionsType<GetAffiliatesResponseType> {
  search?: string
}
export const useGetAllAffiliates = ({ queryOptions, search }: UseGetAllAffiliatesProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.AFFILIATES, search],
    queryFn: async () => {
      const { data } = await getAllAffiliates(search)
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  })

export interface UseGetAllCustomerMembershipsProps
  extends GenericQueryOptionsType<GetCustomerMembershipsResponseType> {
  customerId: string
  isIntegratedPage: boolean
  filters: Record<string, string | number | boolean>
}
export const useGetAllCustomerMemberships = ({
  queryOptions,
  filters,
  customerId,
  isIntegratedPage,
}: UseGetAllCustomerMembershipsProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.CUSTOMER_MEMBERSHIPS, customerId, isIntegratedPage, filters],
    queryFn: async () => {
      const { data } = await getAllCustomerMemberships(customerId, filters, isIntegratedPage)
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  })

export interface UseGetParticipantsFromCartProps
  extends GenericQueryOptionsType<GetParticipantsFromCart> {
  cartId: string
  isIntegratedPage?: boolean
}
export const useGetAllParticipantsFromCart = ({
  cartId,
  isIntegratedPage,
  queryOptions,
}: UseGetParticipantsFromCartProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.CART_PARTICIPANTS, cartId, isIntegratedPage],
    queryFn: async () => {
      const { data } = await getParticipantsFromCart(cartId, isIntegratedPage)
      return data
    },
    placeholderData: keepPreviousData,
  })

export interface UseGetBookingFieldsOfCartProps
  extends GenericQueryOptionsType<GetPerBookingFieldsOfCartResponseType> {
  cartId: string
}
export const useGetBookingFieldsOfCart = ({
  cartId,
  queryOptions,
}: UseGetBookingFieldsOfCartProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.CART_BOOKING_FIELDS, cartId],
    queryFn: async () => {
      const { data } = await getPerBookingFieldsOfCart(cartId)
      return data
    },
    placeholderData: keepPreviousData,
  })

export interface UseGetOrderFieldsProps
  extends GenericQueryOptionsType<GetOrderFieldsResponseType> {
  domainId: string
}
export const useGetOrderFields = ({ domainId, queryOptions }: UseGetOrderFieldsProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.ORDER_FIELDS, domainId],
    queryFn: async () => {
      const { data } = await getDomainOrderFieldbyId(domainId)
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  })

export interface UseGetProductFieldsProps
  extends GenericQueryOptionsType<GetProductFieldsResponseType> {
  productId: string
  isIntegratedPage?: boolean
}
export const useGetProductFields = ({
  productId,
  isIntegratedPage,
  queryOptions,
}: UseGetProductFieldsProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.PRODUCT_FIELDS, productId, isIntegratedPage],
    queryFn: async () => {
      const { data } = await getProductFields(productId, isIntegratedPage)
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  })

export interface UseGetGiftcardValidaityProps
  extends GenericQueryOptionsType<GiftCardValidityResponseType> {
  productId: string
}
export const UseGetGiftcardValidaity = ({
  productId,
  queryOptions,
}: UseGetGiftcardValidaityProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.GIFTCARD_VALIDITY, productId],
    queryFn: async () => {
      const { data } = await getGiftCardValidity(productId)
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  })

export interface UseGetProductAddonsProps
  extends GenericQueryOptionsType<GetProductAddonsResponseType> {
  productId: string
  isIntegratedPage?: boolean
  filters?: Record<string, string | number | boolean>
}
export const useGetProductAddons = ({
  productId,
  isIntegratedPage,
  filters,
  queryOptions,
}: UseGetProductAddonsProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.PRODUCT_ADDONS, productId, isIntegratedPage],
    queryFn: async () => {
      const { data } = await getProductAddons(productId, isIntegratedPage, filters)
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  })

export const useAddEditAddonsOfCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  payload: AddEditAddonsRequestType
  cartId: string
  cartItemId: string
}>) =>
  useMutation({
    mutationFn: async ({ cartId, cartItemId, payload }) => {
      const { data } = await addEditAddonsOfCart(cartId, cartItemId, payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
        data: data?.data,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useAddEditExpenseDiscountOfCartItemMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<{
  payload: CreateEditExpenseDiscountRequestType
  cartId: string
}>) =>
  useMutation({
    mutationFn: async ({ cartId, payload }) => {
      const { data } = await createEditExpenseDiscount(cartId, payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      if (data?.data)
        await getQueryClient().setQueryData([CacheKeys.CART, variables.cartId], {
          data: data?.data,
        })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useAddParticipantToCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    payload: AddParticipantsToCartRequestType
    cartId: string
    isIntegratedPage: boolean
  },
  AddParticipantsToCartResponseType
>) =>
  useMutation({
    mutationFn: async ({ cartId, payload, isIntegratedPage = false }) => {
      const { data } = await addParticipantsToCart(cartId, payload, isIntegratedPage)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().invalidateQueries({
        queryKey: [CacheKeys.CART_PARTICIPANTS, variables.cartId, variables.isIntegratedPage],
        exact: true,
        refetchType: "all",
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useAddUpdateBookingFieldsToCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    payload: UpdatePerBookingFieldsOfCartRequestType["body"]
    cartId: string
    cartItemId: string
    isIntegratedPage: boolean
  },
  UpdatePerBookingFieldsOfCartResponseType
>) =>
  useMutation({
    mutationFn: async ({ cartId, cartItemId, payload, isIntegratedPage = false }) => {
      const { data } = await updatePerBookingFieldsOfCart(
        {
          body: payload,
          cartId,
          cartItemId,
        },
        isIntegratedPage
      )
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().invalidateQueries({
        queryKey: [CacheKeys.CART_BOOKING_FIELDS, variables.cartId],
        exact: true,
        refetchType: "all",
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useAddNoteMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    payload: AddNoteRequestType
  },
  AddNoteResponseType
>) =>
  useMutation({
    mutationFn: async ({ payload }) => {
      const { data } = await addNote(payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().invalidateQueries({
        queryKey: [
          CacheKeys.NOTES,
          variables?.payload?.notable_id,
          variables?.payload?.notable_type,
        ],
        exact: true,
        refetchType: "all",
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export interface UseGetAllNotesProps extends GenericQueryOptionsType<GetAllNotesResponseType[]> {
  notable_id: string
  notable_type: NoteTypesEnum
}
export const useGetAllNotes = ({ notable_id, notable_type, queryOptions }: UseGetAllNotesProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.NOTES, notable_id, notable_type],
    queryFn: async () => {
      const { data } = await getAllNotes({
        notable_id,
        notable_type,
      })
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 30,
  })

export interface UseGetIntegrationsProps
  extends GenericQueryOptionsType<GetIntegrationResponseType> {
  isIntegratedPage: boolean
  type?: string
  installed?: boolean
  is_installable?: boolean
}
export const useGetIntegrations = ({
  isIntegratedPage,
  type,
  installed,
  is_installable,
  queryOptions,
}: UseGetIntegrationsProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [CacheKeys.INTEGRATIONS, installed, is_installable, type, isIntegratedPage],
    queryFn: async () => {
      const { data } = await getIntegrations(
        {
          installed,
          is_installable,
          type,
        },
        isIntegratedPage
      )
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 30,
  })

export const useCloneOrderToCartMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    orderId: string
  },
  CreateCartResponseType
>) =>
  useMutation({
    mutationFn: async ({ orderId }) => {
      const { data } = await cloneOrderToCart(orderId)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      await getQueryClient().setQueryData([CacheKeys.CART, data?.cart?.id], {
        data: data?.cart,
      })
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useValidateOfferMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    orderId: string
    orderItemId: string
    payload: CustomerRebookRequestType
  },
  ValidateOfferResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, orderItemId, payload }) => {
      const { data } = await validateOffer(orderId, orderItemId, payload)
      return data
    },
    ...mutateOptions,
    onSuccess: async (data, variables, context) => {
      mutateOptions?.onSuccess?.(data, variables, context)
    },
  })

export const useGetAvailabilityForMonth = ({
  dateRanges,
  filters,
  queryOptions,
}: ICalendarEventParams) => {
  return useQueries({
    queries: dateRanges.map((range) => {
      return {
        ...queryOptions,
        queryKey: ["orders", "availabilityForMonth", range, filters],
        queryFn: async () => {
          const { data: allEventsResponse } = await getAvailabilityForMonth({
            ...range,
            ...filters,
          })
          if (allEventsResponse.length) {
            return allEventsResponse
          }
          return []
        },
      }
    }),
    combine: (results) => {
      // Create a map to store products by their ID
      const productMap = new Map<string, GetAllEventsResponseType>()

      // Process each result and merge availabilities for the same product
      results.forEach((result) => {
        if (result.data) {
          result.data.forEach((product) => {
            const existingProduct = productMap.get(product.product_id)

            if (existingProduct) {
              // Merge availabilities for existing product
              const mergedAvailabilities = [
                ...existingProduct.availability,
                ...product.availability,
              ]

              // Update the product with merged availabilities
              productMap.set(product.product_id, {
                ...existingProduct,
                availability: mergedAvailabilities,
              })
            } else {
              // Add new product to map
              productMap.set(product.product_id, product)
            }
          })
        }
      })

      // Convert map values back to array
      return {
        data: Array.from(productMap.values()),
        isLoading: results.some((result) => result.isLoading || result.isFetching),
        isError: results.some((result) => result.isError),
        error: results.find((result) => result.isError)?.error,
        refetch: () => {
          return results.map((result) => result.refetch())
        },
      }
    },
  })
}
